import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Megaphone, Plus, Trash2 } from "lucide-react";

const TARGETS = ["all", "student", "parent", "teacher", "accountant", "hr", "principal", "admin"];

export default function AnnouncementsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", target_role: "all", target_class: "" });

  const canManage = ["admin", "principal", "teacher"].includes(user?.role);

  const load = async () => {
    const { data } = await api.get("/announcements");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/announcements", form);
      toast.success("Announcement posted");
      setOpen(false);
      setForm({ title: "", body: "", target_role: "all", target_class: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    await api.delete(`/announcements/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="space-y-6" data-testid="announcements-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Comms</div>
          <h1 className="text-4xl font-bold tracking-tight">Announcements</h1>
          <p className="text-muted-foreground text-sm mt-1">School-wide and targeted messages.</p>
        </div>
        {canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="rounded-2xl gap-2" data-testid="ann-create-open"><Plus className="h-4 w-4" /> New</Button></DialogTrigger>
            <DialogContent className="rounded-2xl">
              <DialogHeader><DialogTitle>Post announcement</DialogTitle></DialogHeader>
              <div className="space-y-3 py-2">
                <Input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-2xl" data-testid="ann-title" />
                <Textarea placeholder="Body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="rounded-2xl min-h-32" />
                <Select value={form.target_role} onValueChange={(v) => setForm({ ...form, target_role: v })}>
                  <SelectTrigger className="rounded-2xl"><SelectValue placeholder="Audience" /></SelectTrigger>
                  <SelectContent>{TARGETS.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <DialogFooter><Button onClick={create} className="rounded-2xl" data-testid="ann-create-submit">Post</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {items.length === 0 && <div className="col-span-full text-center py-16 text-sm text-muted-foreground">No announcements.</div>}
        {items.map((a) => (
          <Card key={a.id} className="rounded-2xl lift" data-testid={`ann-${a.id}`}>
            <CardHeader className="flex flex-row items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-primary/15 text-primary grid place-items-center"><Megaphone className="h-5 w-5" /></div>
                <div>
                  <CardTitle className="text-lg">{a.title}</CardTitle>
                  <Badge variant="outline" className="rounded-full mt-1 text-[0.65rem] capitalize">{a.target_role}</Badge>
                </div>
              </div>
              {canManage && (
                <Button size="icon" variant="ghost" onClick={() => remove(a.id)} className="text-red-500" data-testid={`ann-delete-${a.id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground leading-relaxed">{a.body}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
